import { TFunction } from 'i18next'
import { z } from 'zod'

import { hasValidVatNumber } from '@features/invoices/utils/hasValidVatNumber'

import { getRegistrationNumberRegex } from '../../../../../utils'
import { getVatNumberFieldInvalidFormatErrorMessage } from '../../../../invoices/utils/getVatNumberFieldInvalidFormatErrorMessage'
import { MAX_COMPANY_NAME_LENGTH } from '../../../constants/maxCompanyNameLength'
import { FrenchLegalStatus } from '../../../enums/frenchLegalStatus'
import { getCompanyContactFormSchema } from '../../../utils/getCompanyContactFormSchema'
import { CompanyInformationFormSchema } from '../types/companyInformationFormSchema'

export const getCompanyInformationFormSchema = (t: TFunction, countryId = 'DK'): CompanyInformationFormSchema =>
  z
    .object({
      addressLine2: z.string().optional(),
      autoCompletedFields: z.record(z.string(), z.boolean()).optional(),
      companyAddress: z.string().optional(),
      companyCity: z.string().optional(),
      companyName: z
        .string()
        .min(1, { message: t('external_invoices.company_information_modal.company_name_required') })
        .max(MAX_COMPANY_NAME_LENGTH),
      companyZipCode: z.string().optional(),
      isSiretPendingAssignment: z.boolean().optional(),
      legalStatusCustom: z.string().optional(),
      legalStatusOption: z.nativeEnum(FrenchLegalStatus).optional(),
      registrationNo: z
        .string()
        .optional()
        .refine((val) => !val || getRegistrationNumberRegex(countryId).test(val), {
          message: t(`settings.organization.organization_info.basic_info.registration_number.org_${countryId}.error`),
        }),
      sharedCapital: z.number().optional(),
      vatNo: z.string().optional(),
      isVatExempted: z.boolean(),
    })
    .merge(getCompanyContactFormSchema(t))
    .superRefine((data, ctx) => {
      if (data.legalStatusOption === FrenchLegalStatus.Other && !data.legalStatusCustom) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t('settings.organization.organization_info.basic_info.legal_status_custom.error'),
          path: ['legalStatusCustom'],
        })
      }
      if (data.vatNo && !hasValidVatNumber(data.vatNo, countryId)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: getVatNumberFieldInvalidFormatErrorMessage(countryId, t),
          path: ['vatNo'],
        })
      }
    })
